import CustomerAPI from 'api/customer.api';
import { ExternalUserAPI } from 'api/external.user.api';
import { SharedUserAPI } from 'api/shared.user.api';
import { updateLoadingState } from 'store/app.slice';
import { updateCustomer, updateCustomerTheme } from 'store/auth.slice';
import {
  appendExternalUserList,
  setExternalUserList,
  updateExternalUserList,
  updateExternalUserShowOnlyProductiveTrips,
} from 'store/external.user.slice';
import { updateInternalUserShowOnlyProductiveTrips } from 'store/internal.user.slice';
import { type IUpdateCustomerPayload } from 'types/payloads/customer.payload.types';
import {
  type ICreateExternalUserPayload,
  type IUpdateExternalUserPayload,
} from 'types/payloads/user.payload.types';
import { type ExternalUser } from 'types/user.types';
import { BaseHandler } from './base.handler';
import { DEFAULT_DRIVER_BEHAVIOR_THRESHOLDS } from 'utils/constants/driverbehaviour';

export default class ExternalUserHandler extends BaseHandler {
  private readonly API: ExternalUserAPI = new ExternalUserAPI();
  private readonly CustomerAPI: CustomerAPI = new CustomerAPI();
  private readonly SharedUserAPI: SharedUserAPI = new SharedUserAPI();

  async createExternalUser(payload: ICreateExternalUserPayload): Promise<void> {
    this.dispatch(updateLoadingState(true));

    try {
      const newUser = await this.API.create(payload);

      this.dispatch(appendExternalUserList(newUser));

      this.handleSuccess(
        'A new user has been created and a password creation email has been sent.'
      );
    } catch (_) {
      // show snackbar error
      this.handleError('An error occurred while creating the user.');
    }
  }

  async getAll(): Promise<ExternalUser[] | []> {
    this.dispatch(updateLoadingState(true));

    try {
      const userList = await this.API.getAll();

      const transformedUserList = userList.map((user: any) => ({
        ...user,
        showOnlyProductiveTrips: user.showUnproductiveTrips === false,
      }));

      this.dispatch(setExternalUserList(transformedUserList));

      this.dispatch(updateLoadingState(false));

      return transformedUserList;
    } catch (_) {
      this.dispatch(updateLoadingState(false));
    }

    return [];
  }

  async updateExternalUser(
    userId: string,
    payload: IUpdateExternalUserPayload
  ): Promise<void> {
    this.dispatch(updateLoadingState(true));

    try {
      const updatedUser = await this.API.update({
        ...payload,
        userId,
      });

      this.dispatch(updateExternalUserList(updatedUser));

      this.handleSuccess('User has been updated successfully.');
    } catch (_) {
      this.handleError('An error occurred while updating the user.');
    }
  }

  async deactivateExternalUser(
    userId: string,
    firstName: string,
    lastName: string
  ): Promise<void> {
    this.dispatch(updateLoadingState(true));

    try {
      const updatedUser = await this.API.toggleActiveExternalUser(
        userId,
        false
      );

      this.dispatch(updateExternalUserList(updatedUser));

      this.handleSuccess(`${firstName} ${lastName} has been deactivated.`);
    } catch (_) {
      // show snackbar error
      this.handleError('An error occurred while deactivating the user.');
    }
  }

  async activateExternalUser(
    userId: string,
    firstName: string,
    lastName: string
  ): Promise<void> {
    this.dispatch(updateLoadingState(true));

    try {
      const updatedUser = await this.API.toggleActiveExternalUser(userId, true);

      this.dispatch(updateExternalUserList(updatedUser));

      this.handleSuccess(`${firstName} ${lastName} has been activated.`);
    } catch (_) {
      // show snackbar error
      this.handleError('An error occurred while activating the user.');
    }
  }

  async currentCustomer(): Promise<any> {
    this.dispatch(updateLoadingState(true));

    try {
      // Get the customer from the DB
      const customer = await this.API.currentCustomer();

      // Save the information to redux
      this.dispatch(
        updateCustomer({
          id: customer.customerId,
          name: customer.customerName,
          code: customer.customerCode,
          contactFirstName: customer.contactFirstName,
          contactLastName: customer.contactLastName,
          email: customer.email,
          phoneNumber: customer.phoneNumber,
          internalAccountingLink: customer.internalAccountingLink,
          unitSystem: customer.unitSystem,
          timezone: customer.timeZone,
          mineType: customer.mineType ?? '',
          isTiresModule: customer.isTiresModule ?? false,
          proj4String: customer.proj4String ?? null,
          localOriginEasting: customer.localOriginEasting ?? null,
          localOriginNorthing: customer.localOriginNorthing ?? null,
          driverBehavior:
            customer.driverBehavior ?? DEFAULT_DRIVER_BEHAVIOR_THRESHOLDS,
          modules: customer.modules ?? {
            isPartsModule: false,
            isTiresModule: customer.isTiresModule ?? false,
            isMachinesModule: false,
          },
        })
      );
    } catch (_) {
      // show snackbar error
      this.handleError('An error occurred while receiving customer.');
    }
  }

  /**
   * Update the current customer of the user
   */
  async updateCurrentCustomer(payload: IUpdateCustomerPayload) {
    this.dispatch(updateLoadingState(true));

    try {
      // Update the information
      const customer = await this.CustomerAPI.updateCustomer(payload);

      // Save the information
      this.dispatch(
        updateCustomer({
          id: customer.customerId,
          name: customer.customerName,
          code: customer.customerCode,
          contactFirstName: customer.contactFirstName,
          contactLastName: customer.contactLastName,
          email: customer.email,
          phoneNumber: customer.phoneNumber,
          internalAccountingLink: customer.internalAccountingLink,
          unitSystem: customer.unitSystem,
          timezone: customer.timeZone,
          mineType: customer.mineType ?? '',
          isTiresModule: customer.isTiresModule,
          proj4String: customer.proj4String ?? null,
          localOriginEasting: customer.localOriginEasting ?? null,
          localOriginNorthing: customer.localOriginNorthing ?? null,
          driverBehavior:
            customer.driverBehavior ?? DEFAULT_DRIVER_BEHAVIOR_THRESHOLDS,
        })
      );

      this.handleSuccess('Customer information has been updated');
    } catch (_) {
      this.handleError('An error occured while updating customer information.');
    }
  }

  async updateUserTheme(theme: string) {
    this.dispatch(updateLoadingState(true));

    try {
      await this.SharedUserAPI.updateUserTheme(theme);
      this.dispatch(updateCustomerTheme({ theme }));
      this.handleSuccess('Theme has been updated');
    } catch (_) {
      this.handleError('An error occured while updating theme selection.');
    }
  }

  async updateShowUnproductiveTripsPreference(payload: {
    showUnproductiveTrips: boolean;
    userId: string;
    userReducerLocation: string;
  }) {
    this.dispatch(updateLoadingState(true));

    try {
      await this.SharedUserAPI.updateShowUnproductiveTrips(
        payload.showUnproductiveTrips
      );

      const newShowOnlyProductiveTrips = !payload.showUnproductiveTrips;

      if (payload.userReducerLocation === 'internalUserReducer') {
        this.dispatch(
          updateInternalUserShowOnlyProductiveTrips({
            userId: payload.userId,
            showOnlyProductiveTrips: newShowOnlyProductiveTrips,
          })
        );
      } else {
        this.dispatch(
          updateExternalUserShowOnlyProductiveTrips({
            userId: payload.userId,
            showOnlyProductiveTrips: newShowOnlyProductiveTrips,
          })
        );
      }

      this.handleSuccess('Trip filter preference has been updated');
    } catch (error) {
      console.error('[Handler] Error updating trip filter:', error);
      this.handleError(
        'An error occured while updating trip filter preference.'
      );
    } finally {
      this.dispatch(updateLoadingState(false));
    }
  }
}
